import { cn } from '@/lib/utils';

/**
 * Diagnosis Icon Component
 *
 * A customizable diagnosis/medical sparkle icon for medical diagnostics
 *
 * @example
 * ```tsx
 * <Diagnosis />
 * <Diagnosis className="text-blue-500" size={24} />
 * <Diagnosis fill="currentColor" width={20} height={20} />
 * <Diagnosis className="hover:text-pranik-brand-dark cursor-pointer" onClick={handleDiagnosis} />
 * ```
 */

export interface MedicationsProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon (sets both width and height)
   * @default 16
   */
  size?: number;
  /**
   * Custom width (overrides size)
   */
  width?: number;
  /**
   * Custom height (overrides size)
   */
  height?: number;
  /**
   * Fill color for the icon
   * Use "currentColor" to inherit text color from parent
   * @default "currentColor"
   */
  fill?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Accessible label for screen readers
   */
  'aria-label'?: string;
}

export function Medications({
  size = 16,
  width,
  height,
  fill = 'currentColor',
  className,
  'aria-label': ariaLabel,
  ...props
}: MedicationsProps) {
  const w = width ?? size;
  const h = height ?? size;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      viewBox="0 0 20 24"
      fill="none"
      className={cn('inline-block flex-shrink-0 transition-colors', className)}
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.292 12.5737C15.0335 11.7619 17.1038 12.5149 17.916 14.2563C18.7279 15.9981 17.9739 18.0692 16.2324 18.8813L10.3789 21.6108C8.63723 22.423 6.56625 21.6697 5.75389 19.9282C4.94172 18.1864 5.6957 16.1155 7.43748 15.3032L13.292 12.5737ZM8.1035 16.7329C7.15124 17.1772 6.73885 18.3088 7.1826 19.2612C7.62693 20.2136 8.75944 20.6261 9.71189 20.1821L11.8887 19.1665L10.2812 15.7173L8.1035 16.7329ZM16.4873 14.9233C16.0431 13.9708 14.9106 13.5584 13.958 14.0024L11.7099 15.0503L13.3174 18.4995L15.5664 17.4526C16.5187 17.0085 16.9309 15.8758 16.4873 14.9233ZM3.916 3.78662C4.48938 2.84904 5.91315 2.94752 6.34178 3.96143L9.06736 10.4097C9.45855 11.3359 8.7088 12.3184 7.71092 12.2476L1.32322 11.7925C0.322744 11.7211 -0.271394 10.6395 0.253888 9.77979L3.916 3.78662ZM15.3818 2.06201C17.898 2.06225 19.9385 4.10234 19.9385 6.61865C19.9382 9.13476 17.8979 11.1751 15.3818 11.1753C12.8656 11.1753 10.8254 9.13491 10.8252 6.61865C10.8252 4.1022 12.8654 2.06201 15.3818 2.06201ZM1.81541 10.2456L7.45604 10.6489L5.04881 4.95361L1.81541 10.2456ZM12.4179 6.92432C12.5712 8.42623 13.8395 9.59814 15.3818 9.59814C16.3841 9.598 17.2685 9.10122 17.8086 8.34229C17.6924 8.37231 17.5696 8.37407 17.4463 8.33936L12.4179 6.92432ZM15.3818 3.63818C14.3552 3.63818 13.4498 4.15747 12.914 4.94775L17.8242 6.32861C18.0549 6.39353 18.2404 6.56899 18.3545 6.79834C18.358 6.73887 18.3613 6.67901 18.3613 6.61865C18.3613 4.97307 17.0273 3.63842 15.3818 3.63818Z"
        fill={fill}
      />
    </svg>
  );
}
